import React, { FunctionComponent } from "react";

import { contentTypes } from "src/components/index/BrowseAndExploreBanner";
import {
  IndexSearchQuery,
  ReactSelectOption,
  BehaviorLanguages,
  SearchType,
} from "src/lib/globalTypes";
import { SearchableTypeName, Subject } from "src/lib/graphql/types";
import Checkbox from "src/components/Checkbox";

import SubjectDropdown from "../SubjectDropdown";
import Dropdown from "../Dropdown";
import CircleIcon from "../icons/CircleIcon";
import TooltipInfoIcon from "../TooltipInfoIcon";

type SearchResultsSidebarProps = {
  query: IndexSearchQuery;
  subjects: Subject[];
  setType: (type: SearchableTypeName | "all") => void;
  setFilter: (key: string, value?: string) => void;
  bannerMessage?: string;
};

const SearchResultsSidebar: FunctionComponent<SearchResultsSidebarProps> = ({
  query,
  subjects,
  setType,
  setFilter,
  bannerMessage,
}) => {
  const languageOptions: ReactSelectOption[] = [
    { label: "All", value: "all" },
    ...BehaviorLanguages.map((lng) => ({
      label: lng,
      value: lng,
    })),
  ];

  return (
    <div className="search-results-sidebar content-min-height">
      {bannerMessage && (
        <div className="search-results-banner">{bannerMessage}</div>
      )}

      <div className="type-select">
        <div className="filter-title">TYPE</div>
        <div
          className={
            "type-filter" + (query.searchType === "search" ? " active" : "")
          }
          onClick={() => setType("all")}
        >
          <CircleIcon />
          All
        </div>
        {contentTypes.map((type) => (
          <div
            className={
              `type-filter ${type.type}` +
              (query.searchType === type.plural ? " active" : "")
            }
            onClick={() => setType(type.type as SearchableTypeName)}
            key={type.type}
          >
            {type.icon}
            {type.label}
          </div>
        ))}
      </div>
      {!["schemas", "models"].includes(query?.searchType ?? "") && (
        <div className="subject-select">
          <div className="filter-title">
            RELATES TO{query?.searchType === "search" && "*"}
          </div>
          <SubjectDropdown
            options={[
              { id: "all", name: "All" } as Subject,
              ...(subjects || []),
            ]}
            value={query.subject}
            id="subjects"
            onChange={(value) =>
              setFilter(
                "subject",
                value === "All" ? undefined : (value as string)
              )
            }
            placeholder={"Which thing?"}
          />
          <div className="footnote">
            {query?.searchType !== "all" &&
              "* Applies to Datasets and Behaviors"}
          </div>
        </div>
      )}
      {query?.searchType === "behaviors" && (
        <div className="dataset-filter-select">
          <div className="filter-title">LANGUAGE</div>
          <Dropdown
            options={languageOptions || []}
            value={languageOptions?.find((opt) => opt.value === query.language)}
            onChange={(opt) => setFilter("language", opt.value)}
            placeholder={"Behavior is in..."}
          />
        </div>
      )}
      {query?.searchType !== "schemas" && (
        <div className="dataset-filter-select">
          <div className="filter-title">PROJECT STATUS</div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: "0.5em" }}>
              <Checkbox
                checked={query?.releasedOnly === "true"}
                onClick={(newStatus) => {
                  setFilter("releasedOnly", (newStatus as boolean).toString());
                }}
              >
                Released only
              </Checkbox>
            </span>
            <span className="search-label-tooltip">
              <TooltipInfoIcon>
                Limits results to only those projects with versioned releases
                (formal checkpoints accompanied by a changelog).
              </TooltipInfoIcon>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResultsSidebar;
